import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sparkles, Plus, Trash2, Loader2, X, UploadCloud, ArrowUp, ArrowDown, Eye, MousePointerClick, Pencil } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { SpotlightCreative } from '@/components/spotlights/SpotlightCreative';

const EMPTY = {
  title: '', body: '', image_url: '', label: 'Featured', source: 'house', fill_only: false,
  cta_type: 'internal', cta_label: 'Learn more', link_path: '', external_url: '',
  slots: [], active: true, starts_at: '', ends_at: '', order: 0,
};

/**
 * Admin · My Promos — your own house promotions: create, schedule, reorder, switch off.
 * Lifted out of AdminSpotlightsTab in session 62 so the advertising area could become four
 * distinct blocks instead of one long scroll.
 */
export default function AdminHousePromos({ fillSlot = null, onFillConsumed }) {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [placements, setPlacements] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imageBad, setImageBad] = useState(false);
  const [destinationGroups, setDestinationGroups] = useState([]);
  const destinations = destinationGroups.flatMap((g) => g.options);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/spotlights`, { headers });
      setPlacements(res.data.placements || []);
      setSlots(res.data.slots || []);
    } catch (err) {
      toast.error('Failed to load spotlights');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setImageBad(false); }, [form.image_url]);

  useEffect(() => {
    axios.get(`${API}/admin/spotlights/destinations`, { headers })
      .then((res) => setDestinationGroups(res.data.groups || []))
      .catch(() => setDestinationGroups([]));
  }, []);

  // "Put a promo here" on an empty space in the Ad Spaces block.
  useEffect(() => {
    if (!fillSlot) return;
    setForm({ ...EMPTY, slots: [fillSlot], fill_only: true });
    setEditId(null);
    setShowForm(true);
    onFillConsumed?.();
  }, [fillSlot]);

  const openCreate = () => { setForm(EMPTY); setEditId(null); setShowForm(true); };
  const openEdit = (p) => {
    setForm({ ...EMPTY, ...p, starts_at: (p.starts_at || '').slice(0, 10), ends_at: (p.ends_at || '').slice(0, 10) });
    setEditId(p.id); setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditId(null); setForm(EMPTY); };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload-photo`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setForm((f) => ({ ...f, image_url: res.data.file_url }));
      toast.success('Image uploaded');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const toggleSlot = (key) =>
    setForm((f) => ({ ...f, slots: f.slots.includes(key) ? f.slots.filter((s) => s !== key) : [...f.slots, key] }));

  const save = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    if (form.slots.length === 0) { toast.error('Pick at least one placement slot'); return; }
    if (form.cta_type === 'external' && !form.external_url.trim()) { toast.error('External URL required'); return; }
    if (form.cta_type === 'internal' && !form.link_path.trim()) { toast.error('Internal link path required'); return; }
    setSaving(true);
    try {
      if (editId) await axios.patch(`${API}/admin/spotlights/${editId}`, form, { headers });
      else await axios.post(`${API}/admin/spotlights`, form, { headers });
      toast.success(editId ? 'Spotlight updated' : 'Spotlight created');
      closeForm(); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const toggleActive = async (p) => {
    try { await axios.patch(`${API}/admin/spotlights/${p.id}`, { active: !p.active }, { headers }); load(); }
    catch (err) { toast.error('Update failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this spotlight?')) return;
    try { await axios.delete(`${API}/admin/spotlights/${id}`, { headers }); toast.success('Deleted'); load(); }
    catch (err) { toast.error('Delete failed'); }
  };

  const move = async (idx, dir) => {
    const next = [...placements];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setPlacements(next);
    try { await axios.post(`${API}/admin/spotlights/reorder`, { ordered_ids: next.map((p) => p.id) }, { headers }); }
    catch (err) { toast.error('Reorder failed'); load(); }
  };

  const slotLabel = (key) => (slots.find((s) => s.key === key)?.label || key);

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>;

  return (
    <div className="space-y-4" data-testid="admin-house-promos">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-gray-400">Your own promotions, across Marketplace, Workshops &amp; Bookings.</p>
        {!showForm && (
          <Button onClick={openCreate} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="add-spotlight-btn">
            <Plus className="w-4 h-4 mr-1" /> New Spotlight
          </Button>
        )}
      </div>

      {showForm && (
        <Card className="bg-white/5 border-amber-500/30">
          <CardContent className="pt-5 space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="text-sm font-semibold text-gray-200">{editId ? 'Edit' : 'New'} Spotlight</h4>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-200"><X className="w-4 h-4" /></button>
            </div>

            <div>
              <Label className="text-xs text-gray-400">Headline</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-white/5 border-white/10 mt-1" data-testid="spotlight-title-input" />
            </div>
            <div>
              <Label className="text-xs text-gray-400">Body text</Label>
              <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="bg-white/5 border-white/10 mt-1" rows={2} />
            </div>

            <div>
              <Label className="text-xs text-gray-400">Image</Label>
              <div className="mt-1 flex items-center gap-3 flex-wrap">
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10 text-sm text-gray-200">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />} Upload
                  <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={uploading} data-testid="spotlight-image-input" />
                </label>
                <span className="text-xs text-gray-500">or paste URL:</span>
                <Input value={form.image_url || ''} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://…" className="bg-white/5 border-white/10 flex-1 min-w-[180px]" />
              </div>
              {form.image_url && <img src={form.image_url} alt="preview" className="mt-2 h-16 rounded-lg object-cover" />}
              {form.slots.length > 0 && (
                <div className="mt-2 space-y-1" data-testid="spotlight-size-guide">
                  {slots.filter((s) => form.slots.includes(s.key) && s.image).map((s) => (
                    <p key={s.key} className="text-[11px] text-gray-400">
                      <span className="text-gray-300">{s.label}:</span> {s.image.width}×{s.image.height}px ({s.image.ratio}),
                      under {s.image.max_kb}KB — {s.image.note}
                    </p>
                  ))}
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-400">CTA type</Label>
                <Select value={form.cta_type} onValueChange={(v) => setForm({ ...form, cta_type: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 mt-1" data-testid="spotlight-cta-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internal">Internal (in-app link)</SelectItem>
                    <SelectItem value="external">External (new tab)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-gray-400">CTA label</Label>
                <Input value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
              </div>
            </div>

            {form.cta_type === 'internal' ? (
              <div>
                <Label className="text-xs text-gray-400">Where should it send people?</Label>
                {/* Pick the destination — the path is written for you, so it cannot be mistyped. */}
                <Select
                  value={destinations.some((o) => o.path === form.link_path) ? form.link_path : 'custom'}
                  onValueChange={(v) => setForm({ ...form, link_path: v === 'custom' ? '' : v })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 mt-1" data-testid="spotlight-destination-picker">
                    <SelectValue placeholder="Choose a page…" />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    {destinationGroups.map((g) => (
                      <SelectGroup key={g.group}>
                        <SelectLabel>{g.group}</SelectLabel>
                        {g.options.map((o) => (
                          <SelectItem key={o.path} value={o.path}>{o.label}</SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                    <SelectGroup>
                      <SelectLabel>Anything else</SelectLabel>
                      <SelectItem value="custom">Type a path myself…</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {!destinations.some((o) => o.path === form.link_path) && (
                  <>
                    <Input
                      value={form.link_path || ''}
                      onChange={(e) => setForm({ ...form, link_path: e.target.value })}
                      placeholder="/dashboard/…"
                      className="bg-white/5 border-white/10 mt-2"
                      data-testid="spotlight-link-path"
                    />
                    {form.link_path && !form.link_path.startsWith('/') && (
                      <p className="text-[11px] text-red-400 mt-1" data-testid="spotlight-path-warning">
                        A page inside the app must start with /
                      </p>
                    )}
                  </>
                )}
                {form.link_path && (
                  <p className="text-[11px] text-gray-500 mt-1">
                    Members land on <span className="text-amber-300">{form.link_path}</span>
                  </p>
                )}
              </div>
            ) : (
              <div>
                <Label className="text-xs text-gray-400">External URL</Label>
                <Input value={form.external_url || ''} onChange={(e) => setForm({ ...form, external_url: e.target.value })} placeholder="https://magazine.example.com/subscribe" className="bg-white/5 border-white/10 mt-1" data-testid="spotlight-external-url" />
              </div>
            )}

            <div>
              <Label className="text-xs text-gray-400">Placement slots</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {slots.map((s) => (
                  <label key={s.key} className={`text-xs px-2 py-1 rounded-full cursor-pointer border ${form.slots.includes(s.key) ? 'bg-amber-600 text-white border-amber-600' : 'bg-white/5 text-gray-300 border-white/10'}`} data-testid={`spotlight-slot-${s.key}`}>
                    <input type="checkbox" className="hidden" checked={form.slots.includes(s.key)} onChange={() => toggleSlot(s.key)} />
                    {s.label}
                  </label>
                ))}
              </div>
            </div>

            {/* Exactly how it will look, in every space you have picked, before you publish. */}
            {form.slots.length > 0 && (
              <div data-testid="spotlight-form-preview">
                <Label className="text-xs text-gray-400">Preview</Label>
                <div className="mt-2 grid gap-3 md:grid-cols-2">
                  {slots.filter((s) => form.slots.includes(s.key)).map((s) => (
                    <div key={s.key} className="rounded-lg border border-white/10 overflow-hidden">
                      <p className="px-3 py-1.5 text-[11px] text-gray-400 bg-black/30">{s.label}</p>
                      <div className={`p-3 ${s.theme === 'light' ? 'bg-[#faf7f2]' : 'bg-[#0f1116]'} ${s.variant === 'flank' ? 'flex' : ''}`}>
                        <SpotlightCreative
                          p={{ ...form, label: form.label || 'Featured' }}
                          variant={s.variant || 'banner'}
                          theme={s.theme || 'dark'}
                          className={s.variant === 'flank' ? 'w-56' : s.variant === 'card' ? 'max-w-xs' : ''}
                          onImageError={() => setImageBad(true)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                {imageBad && form.image_url && (
                  <p className="mt-2 text-xs text-red-300" data-testid="spotlight-image-broken">
                    That image link doesn&apos;t load — the promo would publish with no picture.
                  </p>
                )}
              </div>
            )}

            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-gray-400">Start date</Label>
                <Input type="date" value={form.starts_at || ''} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
              </div>
              <div>
                <Label className="text-xs text-gray-400">End date</Label>
                <Input type="date" value={form.ends_at || ''} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
              </div>
              <div className="flex items-end gap-2 pb-1">
                <Label className="text-sm text-gray-300">Active</Label>
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="spotlight-active-toggle" />
              </div>
            </div>

            <div className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-lg p-3">
              <Switch checked={!!form.fill_only} onCheckedChange={(v) => setForm({ ...form, fill_only: v })} data-testid="spotlight-fill-only-toggle" />
              <div>
                <Label className="text-sm text-gray-300">Only run while the spot is unsold</Label>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  Keeps the space filled with your own promo, and steps aside automatically the moment a paid ad goes live there.
                </p>
              </div>
            </div>

            <Button onClick={save} disabled={saving || uploading} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-spotlight-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} {editId ? 'Update' : 'Create'} Spotlight
            </Button>
          </CardContent>
        </Card>
      )}

      {placements.length === 0 ? (
        <Card className="bg-white/5 border-white/10"><CardContent className="py-10 text-center text-gray-500"><Sparkles className="w-8 h-8 mx-auto mb-2 opacity-30" /> No spotlights yet.</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {placements.map((p, idx) => (
            <Card key={p.id} className="bg-white/5 border-white/10" data-testid={`spotlight-row-${p.id}`}>
              <CardContent className="py-3 flex items-center gap-4">
                <div className="flex flex-col">
                  <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-gray-400 hover:text-white disabled:opacity-20"><ArrowUp className="w-3 h-3" /></button>
                  <button onClick={() => move(idx, 1)} disabled={idx === placements.length - 1} className="text-gray-400 hover:text-white disabled:opacity-20"><ArrowDown className="w-3 h-3" /></button>
                </div>
                {p.image_url ? <img src={p.image_url} alt="" className="w-12 h-12 rounded object-cover" /> : <div className="w-12 h-12 rounded bg-amber-500/10 flex items-center justify-center"><Sparkles className="w-4 h-4 text-amber-300/50" /></div>}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300">{p.label}</span>
                    {!p.active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-500/20 text-gray-400">Off</span>}
                  </div>
                  <p className="text-gray-100 font-medium mt-1 truncate">{p.title}</p>
                  <p className="text-[11px] text-gray-500 truncate">{(p.slots || []).map(slotLabel).join(' · ')}</p>
                </div>
                <div className="hidden sm:flex items-center gap-4 text-xs text-gray-400">
                  <span className="flex items-center gap-1" title="Impressions"><Eye className="w-3 h-3" /> {p.impressions || 0}</span>
                  <span className="flex items-center gap-1" title="Clicks"><MousePointerClick className="w-3 h-3" /> {p.clicks || 0}</span>
                  <span title="Click-through rate">{p.ctr || 0}%</span>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={p.active} onCheckedChange={() => toggleActive(p)} data-testid={`spotlight-toggle-${p.id}`} />
                  <button onClick={() => openEdit(p)} className="text-gray-300 hover:text-white" data-testid={`spotlight-edit-${p.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(p.id)} className="text-red-400 hover:text-red-300" data-testid={`spotlight-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
